import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  signal,
} from '@angular/core';
import { NgStyle } from '@angular/common';
import { KirigamiTourStepHostService } from './tour-step-host.service';

const PADDING = 6; // px halo around the highlighted element

@Component({
  selector: 'kirigami-tour-backdrop',
  standalone: true,
  imports: [NgStyle],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    @if (host.active(); as v) {
      <div class="kt-backdrop" aria-hidden="true">
        <div class="kt-backdrop__hole" [ngStyle]="holeStyle()"></div>
      </div>
    }
  `,
  styles: [
    `
      .kt-backdrop {
        position: fixed;
        inset: 0;
        z-index: 1000;
        pointer-events: none;
      }
      .kt-backdrop__hole {
        position: fixed;
        border-radius: 8px;
        box-shadow: 0 0 0 9999px rgba(15, 23, 42, 0.6);
      }
    `,
  ],
})
export class KirigamiTourBackdropComponent {
  readonly host = inject(KirigamiTourStepHostService);

  /** Bumped every animation frame while a step is active so the cutout tracks
   *  scrolling AND layout shifts (e.g. the instructions panel pushing the page). */
  private readonly tick = signal(0);

  readonly holeStyle = computed(() => {
    this.tick(); // re-read on scroll/resize
    const v = this.host.active();
    if (!v) return {};
    const r = v.anchorEl.getBoundingClientRect();
    return {
      top: `${r.top - PADDING}px`,
      left: `${r.left - PADDING}px`,
      width: `${r.width + PADDING * 2}px`,
      height: `${r.height + PADDING * 2}px`,
    };
  });

  constructor() {
    // While a step is active, recompute the cutout every frame so it follows
    // scrolling and layout shifts (the popover uses floating-ui's animationFrame
    // for the same reason). The loop stops as soon as no step is active.
    effect((onCleanup) => {
      if (!this.host.active()) return;
      let frame = 0;
      const loop = () => {
        this.tick.update((n) => n + 1);
        frame = requestAnimationFrame(loop);
      };
      frame = requestAnimationFrame(loop);
      onCleanup(() => cancelAnimationFrame(frame));
    });
  }
}
